import { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Key, Plus, Search, MoreVertical, Pencil, Trash2, Copy, ArrowUpDown, ArrowUp, ArrowDown, Users, Eye } from 'lucide-react';

export default function AdminTokensPage() {
  const [tokens, setTokens] = useState([]);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editToken, setEditToken] = useState(null);
  const [deleteToken, setDeleteToken] = useState(null);
  const [viewTokenDetails, setViewTokenDetails] = useState(null);
  const [tokenDetailsData, setTokenDetailsData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [form, setForm] = useState({ token_code: '', link_name: '', link_url: '', max_uses: '' });
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => { loadTokens(); }, []);

  const loadTokens = async () => {
    try {
      const res = await adminAPI.getLinkTokens();
      setTokens(res.data);
    } catch {
      toast.error('Errore nel caricamento token');
    } finally {
      setLoading(false);
    }
  };

  const filteredTokens = tokens.filter(t =>
    t.token_code?.toLowerCase().includes(search.toLowerCase()) ||
    t.link_name?.toLowerCase().includes(search.toLowerCase())
  );

  // Sorting logic
  const sortedTokens = [...filteredTokens].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    let aValue, bValue;
    if (sortConfig.key === 'link_name') {
      aValue = a.link_name?.toLowerCase() || '';
      bValue = b.link_name?.toLowerCase() || '';
    } else if (sortConfig.key === 'created_at') {
      aValue = new Date(a.created_at).getTime();
      bValue = new Date(b.created_at).getTime();
    } else {
      return 0;
    }
    
    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-50" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1" /> 
      : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  const resetForm = () => setForm({ token_code: '', link_name: '', link_url: '', max_uses: '' });

  const handleCreate = async () => {
    if (!form.token_code || !form.link_name || !form.link_url) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    try {
      await adminAPI.createLinkToken({
        token_code: form.token_code,
        link_name: form.link_name,
        link_url: form.link_url,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null
      });
      toast.success('Token creato');
      setShowCreate(false);
      resetForm();
      loadTokens();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore creazione token');
    }
  };

  const handleEdit = (token) => {
    setEditToken(token);
    setForm({
      token_code: token.token_code,
      link_name: token.link_name,
      link_url: token.link_url,
      max_uses: token.max_uses ?? ''
    });
  };

  const handleSaveEdit = async () => {
    try {
      await adminAPI.updateLinkToken(editToken.token_id, {
        token_code: form.token_code,
        link_name: form.link_name,
        link_url: form.link_url,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null
      });
      toast.success('Token aggiornato');
      setEditToken(null);
      resetForm();
      loadTokens();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore aggiornamento');
    }
  };

  const handleDelete = async () => {
    try {
      await adminAPI.deleteLinkToken(deleteToken.token_id);
      toast.success('Token eliminato');
      setDeleteToken(null);
      loadTokens();
    } catch {
      toast.error('Errore eliminazione');
    }
  };

  const handleViewDetails = async (token) => {
    setViewTokenDetails(token);
    setLoadingDetails(true);
    try {
      const res = await adminAPI.getTokenDetails(token.token_code);
      setTokenDetailsData(res.data);
    } catch (err) {
      toast.error('Errore nel caricamento dettagli');
      setTokenDetailsData(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const copyToken = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Token copiato negli appunti');
  };

  const TokenFormFields = () => (
    <div className="space-y-4 py-4">
      <div className="space-y-2">
        <Label>Codice Token *</Label>
        <Input
          data-testid="token-code-input"
          placeholder="es. ABC-12345-XYZ"
          value={form.token_code}
          onChange={(e) => setForm({ ...form, token_code: e.target.value })}
        />
        <p className="text-xs text-muted-foreground">
          Questo e' il codice che l'utente dovra' inserire per aggiungere l'OBC
        </p>
      </div>
      <div className="space-y-2">
        <Label>Nome OBC *</Label>
        <Input
          data-testid="token-link-name"
          placeholder="es. Dashboard Analytics"
          value={form.link_name}
          onChange={(e) => setForm({ ...form, link_name: e.target.value })}
        />
        <p className="text-xs text-muted-foreground">
          L'utente vedra' solo questo nome, non l'URL reale
        </p>
      </div>
      <div className="space-y-2">
        <Label>URL OBC *</Label>
        <Input
          data-testid="token-link-url"
          placeholder="https://esempio.com/pagina"
          value={form.link_url}
          onChange={(e) => setForm({ ...form, link_url: e.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label>Utilizzi Massimi (vuoto = illimitato)</Label>
        <Input
          data-testid="token-max-uses"
          type="number"
          min={1}
          placeholder="Illimitato"
          value={form.max_uses}
          onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }} data-testid="tokens-page-title">
            Token OBC
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestisci i token che gli utenti possono riscattare per aggiungere OBC
          </p>
        </div>
        <Button
          data-testid="create-token-btn"
          onClick={() => { resetForm(); setShowCreate(true); }}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="w-4 h-4 mr-2" /> Nuovo Token
        </Button>
      </div>

      <Card className="border-border bg-card">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
              <Key className="w-5 h-5 inline mr-2" />
              Token Disponibili
            </CardTitle>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca token..."
                data-testid="search-tokens"
                className="pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border">
                  <TableHead>Codice Token</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-secondary/50 select-none"
                    onClick={() => handleSort('link_name')}
                  >
                    <div className="flex items-center">
                      Nome OBC
                      <SortIcon columnKey="link_name" />
                    </div>
                  </TableHead>
                  <TableHead>Utilizzi</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-secondary/50 select-none"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center">
                      Data
                      <SortIcon columnKey="created_at" />
                    </div>
                  </TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedTokens.map((token) => (
                  <TableRow key={token.token_id} className="border-border" data-testid={`token-row-${token.token_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="px-2 py-1 rounded bg-secondary text-sm font-mono">
                          {token.token_code}
                        </code>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => copyToken(token.token_code)}
                          data-testid={`copy-token-${token.token_id}`}
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{token.link_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {token.uses_count} / {token.max_uses ?? 'illim.'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(token.created_at).toLocaleDateString('it-IT')}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`token-actions-${token.token_id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-popover border-border">
                          <DropdownMenuItem onClick={() => handleViewDetails(token)} className="cursor-pointer">
                            <Eye className="w-4 h-4 mr-2" /> Visualizza Utenti
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEdit(token)} className="cursor-pointer">
                            <Pencil className="w-4 h-4 mr-2" /> Modifica
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setDeleteToken(token)}
                            className="cursor-pointer text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Elimina
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
                {sortedTokens.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      {loading ? 'Caricamento...' : 'Nessun token configurato'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Nuovo Token OBC</DialogTitle>
          </DialogHeader>
          <TokenFormFields />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annulla</Button>
            <Button data-testid="save-create-token" onClick={handleCreate} className="bg-primary text-primary-foreground">
              Crea Token
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editToken} onOpenChange={() => setEditToken(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Modifica Token</DialogTitle>
          </DialogHeader>
          <TokenFormFields />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditToken(null)}>Annulla</Button>
            <Button data-testid="save-edit-token" onClick={handleSaveEdit} className="bg-primary text-primary-foreground">
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteToken} onOpenChange={() => setDeleteToken(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'Manrope' }}>Conferma Eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Eliminare il token <strong>{deleteToken?.token_code}</strong>? Gli utenti non potranno piu' riscattarlo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-token"
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Token Details Dialog */}
      <Dialog open={!!viewTokenDetails} onOpenChange={() => { setViewTokenDetails(null); setTokenDetailsData(null); }}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              <Users className="w-5 h-5 inline mr-2" />
              Dettagli Token: {viewTokenDetails?.token_code}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {loadingDetails ? (
              <div className="text-center py-8 text-muted-foreground">Caricamento...</div>
            ) : tokenDetailsData ? (
              <div className="space-y-4">
                <div className="p-3 bg-secondary/50 rounded-lg space-y-1">
                  <p className="text-sm"><strong>Nome OBC:</strong> {tokenDetailsData.link_name}</p>
                  <p className="text-sm"><strong>URL:</strong> <span className="text-muted-foreground">{tokenDetailsData.link_url}</span></p>
                  <p className="text-sm">
                    <strong>Utilizzi:</strong>{' '}
                    <Badge variant="outline" className={tokenDetailsData.uses_count !== tokenDetailsData.stored_uses_count ? 'border-yellow-500 text-yellow-500' : ''}>
                      {tokenDetailsData.uses_count} / {tokenDetailsData.max_uses ?? 'illimitati'}
                    </Badge>
                    {tokenDetailsData.uses_count !== tokenDetailsData.stored_uses_count && (
                      <span className="text-xs text-yellow-500 ml-2">(DB: {tokenDetailsData.stored_uses_count})</span>
                    )}
                  </p>
                  <p className="text-sm"><strong>Utenti che l'hanno riscattato:</strong> {tokenDetailsData.redemptions_count}</p>
                </div>
                {tokenDetailsData.redemptions?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border">
                        <TableHead>Utente</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>OBC</TableHead>
                        <TableHead>Data</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tokenDetailsData.redemptions.map((r, idx) => (
                        <TableRow key={idx} className="border-border">
                          <TableCell className="font-medium">{r.user_name}</TableCell>
                          <TableCell className="text-muted-foreground text-sm">{r.user_email}</TableCell>
                          <TableCell>{r.obc_name}</TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {r.created_at ? new Date(r.created_at).toLocaleDateString('it-IT') : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    Nessun utente ha ancora riscattato questo token
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                Errore nel caricamento dei dati
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setViewTokenDetails(null); setTokenDetailsData(null); }}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
